import { TypedUseSelectorHook, useDispatch, useSelector } from 'react-redux';
import type { Action as ReduxAction } from 'redux';
import type { ThunkAction as ReduxThunkAction, ThunkDispatch } from 'redux-thunk';

import type {
	Actions as AllGamesActions,
	State as AllGamesState
} from '~/components/casino-shared/all-games/ducks/types';
import type {
	Actions as ExclusiveGameWidgetsActions,
	State as ExclusiveGameWidgetsState
} from '~/components/casino-shared/exclusive-game-widget/notification/types';
import type { Actions as CasinoActions, State as CasinoState } from '~/components/casino/ducks/types';
import type { Actions as LiveCasinoActions, State as LiveCasinoState } from '~/components/live-casino/ducks/types';
import type { Actions as SearchActions, State as SearchState } from '~/components/search/ducks/types';
import type {
	MultiGameActions,
	State as MultiGameState
} from '~/components/casino-shared/game-loader/multi-game/ducks/types';
import type {
	Actions as SubNavigationActions,
	State as SubNavigationState
} from '~/components/casino-shared/subnavigation/ducks/types';
import type {
	Actions as GameLoaderActions,
	State as GameLoaderState
} from '~/components/casino-shared/game-loader/ducks/types';
import type { Configuration } from '~/configuration/configuration';
import { AppAction, RootState } from '~/store/types';

import { UPDATE_JACKPOT_TIERS } from '~/components/casino-shared/inhouse-jackpot/ducks';
import type { EnhancedGame, Filters, LovedAndRecent, SocketJackpot } from '../types';
import {
	ADD_LOCAL_RECENTLY_PLAYED,
	JACKPOT_SOCKET_CLOSE_CONNECTION,
	JACKPOT_SOCKET_ON_CONNECTION_OPEN,
	JACKPOT_SOCKET_ON_MESSAGE,
	JACKPOT_SOCKET_SET_RETRY_TIMEOUT,
	RESET_ACTIVE_FILTERS,
	RESET_ACTIVE_SORTING,
	SET_ACTIVE_FILTERS,
	SET_ACTIVE_SORTING,
	SET_IS_BALANCE_LIMIT_EXCEEDED_MODAL_SHOWN,
	SET_IS_INSUFFICIENT_CASINO_BALANCE_MODAL_SHOWN,
	SET_IS_ZERO_CASINO_BALANCE_MODAL_SHOWN,
	SET_JACKPOT_DATA,
	SET_LOVED_AND_RECENT,
	SET_SWITCH_TO_REAL,
	UPDATE_LIKES_INFO
} from './action-names';

export type ParsedSocketJackpot = { amount: number; targetTime: string };

export type CasinoSharedState = {
	lovedAndRecentHydrated: boolean;
	lovedAndRecent: LovedAndRecent;
	localRecentlyPlayed: Record<string, EnhancedGame>;
	switchToRealMode: boolean;
	jackpotData: Record<string, { amount: number; provider: string }>;
	isInsufficientCasinoBalanceModalShown: boolean;
	isBalanceLimitExceededModalShown: boolean;
	isZeroCasinoBalanceModalShown: boolean;
	jackpots: Record<string, ParsedSocketJackpot>;
	inHouseJackpotSocketTimeout: null | NodeJS.Timeout;
	jackpotSocketTimeout: null | NodeJS.Timeout;
	activeFilters: Filters;
	activeSortingOption: number;
	initialized?: boolean;
};

export type ExternalActions = {
	type: typeof UPDATE_JACKPOT_TIERS;
	payload: {
		jackpots: SocketJackpot[];
		currency: SocketJackpot['currency'];
	};
};

export type CasinoSharedActions =
	| { type: typeof UPDATE_LIKES_INFO; payload: { gameCode: string; loved: boolean } }
	| { type: typeof SET_LOVED_AND_RECENT; payload: { lovedAndRecent: LovedAndRecent } }
	| { type: typeof ADD_LOCAL_RECENTLY_PLAYED; payload: EnhancedGame }
	| { type: typeof SET_SWITCH_TO_REAL; payload: boolean }
	| {
			type: typeof JACKPOT_SOCKET_ON_CONNECTION_OPEN;
			payload: {
				conn: WebSocket;
				socketType: 'inHouseJackpotSocket' | 'jackpotSocket';
			};
	  }
	| {
			type: typeof JACKPOT_SOCKET_SET_RETRY_TIMEOUT;
			payload: { retryTimeout: NodeJS.Timeout | null; timeoutType: string };
	  }
	| { type: typeof JACKPOT_SOCKET_ON_MESSAGE; payload: { data: SocketJackpot[]; isInHouse: boolean } }
	| {
			type: typeof JACKPOT_SOCKET_CLOSE_CONNECTION;
			payload: { socketType: 'inHouseJackpotSocket' | 'jackpotSocket' };
	  }
	| { type: typeof SET_ACTIVE_FILTERS; payload: Filters }
	| { type: typeof RESET_ACTIVE_FILTERS }
	| { type: typeof SET_ACTIVE_SORTING; payload: number }
	| { type: typeof RESET_ACTIVE_SORTING }
	| { type: typeof SET_JACKPOT_DATA; payload: CasinoSharedState['jackpotData'] }
	| { type: typeof SET_IS_INSUFFICIENT_CASINO_BALANCE_MODAL_SHOWN; payload: boolean }
	| { type: typeof SET_IS_ZERO_CASINO_BALANCE_MODAL_SHOWN; payload: boolean }
	| { type: typeof SET_IS_BALANCE_LIMIT_EXCEEDED_MODAL_SHOWN; payload: boolean }
	| ExternalActions;

// shared selector and dispatch
export type CasinoSharedRootState = RootState & {
	casinoShared: CasinoSharedState;
	casino: CasinoState;
	liveCasino: LiveCasinoState;
	allGames: AllGamesState;
	exclusiveGameWidgets: ExclusiveGameWidgetsState;
	search: SearchState;
	subNavigation: SubNavigationState;
	gameLoader: GameLoaderState;
	multiGame: MultiGameState;

	// TODO remove when this reducers are in RootState
	brand: {
		blockPlayForFun: boolean;
		franchiseId: number;
		isDesktop: boolean;
		enableBannerService: boolean;
		graphyteSearchEnabled: boolean;
	};
	account: { currency: string };
	authentication: { authenticated: boolean };
	translations: Record<string, string>;
	timeLimit: {
		timeLimitExceeded: boolean;
		timePlayed: number;
		timeLeft: number;
		timeLimitReachedText: string;
		timeLimitHydrated: boolean;
	};
};

export type CasinoSharedAction =
	| CasinoSharedActions
	| CasinoActions
	| LiveCasinoActions
	| AllGamesActions
	| ExclusiveGameWidgetsActions
	| SearchActions
	| SubNavigationActions
	| GameLoaderActions
	| MultiGameActions;

export type CasinoSharedThunkAction<
	Actions extends ReduxAction = CasinoSharedAction,
	ReturnType = void,
	ExtraArgs = Configuration
> = ReduxThunkAction<ReturnType, CasinoSharedRootState, ExtraArgs, Actions>;

export type CasinoSharedDispatch = ThunkDispatch<CasinoSharedRootState, Configuration, CasinoSharedAction | AppAction>;

export const useCasinoSharedDispatch: () => CasinoSharedDispatch = useDispatch;
export const useCasinoSharedSelector: TypedUseSelectorHook<CasinoSharedRootState> = useSelector;
